import type { UIEvent } from "react";
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import { fetchTraces } from "../api";
import { Trace, TraceAnnotationCounts, TraceListResponse } from "../types";

interface Filters {
  severity?: string;
  failureMode?: string;
  startDate?: string;
  endDate?: string;
  channel?: string;
  reviewStatus?: string;
}

interface Props {
  selectedTraceId: string | null;
  onSelect: (id: string) => void;
  onDeleteTrace?: (id: string) => void;
  isDeletingTrace?: boolean;
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  failureModes: string[];
  channels: string[];
  onRefresh: () => void;
  isRefreshing: boolean;
  onExport: () => void;
  isExporting: boolean;
  onTracesChange?: (traces: Trace[]) => void;
  annotationCountsByTrace?: Record<string, TraceAnnotationCounts>;
}

export default function TraceList({
  selectedTraceId,
  onSelect,
  onDeleteTrace,
  isDeletingTrace,
  filters,
  onFiltersChange,
  failureModes,
  channels,
  onRefresh,
  isRefreshing,
  onExport,
  isExporting,
  onTracesChange,
  annotationCountsByTrace
}: Props) {
  const query = useInfiniteQuery<TraceListResponse, Error>({
    queryKey: ["traces", filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => fetchTraces({ ...filters, cursor: (pageParam as string | undefined) ?? undefined, limit: 25 }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined
  });

  const traces = useMemo<Trace[]>(() => {
    return (query.data?.pages ?? []).flatMap((page) => page.items ?? []);
  }, [query.data]);

  const listRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  useLayoutEffect(() => {
    const element = listRef.current;
    if (!element) return;
    const updateViewport = () => {
      setViewportHeight(element.clientHeight);
    };
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  };

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return;
      }

      const currentIndex = traces.findIndex((trace) => trace.id === selectedTraceId);

      if (event.key === "ArrowDown" || event.key.toLowerCase() === "j") {
        event.preventDefault();
        const nextIndex = currentIndex >= 0 ? Math.min(currentIndex + 1, traces.length - 1) : 0;
        const nextTrace = traces[nextIndex];
        if (nextTrace) {
          onSelect(nextTrace.id);
        }
      }

      if (event.key === "ArrowUp" || event.key.toLowerCase() === "k") {
        event.preventDefault();
        const nextIndex = currentIndex > 0 ? currentIndex - 1 : 0;
        const nextTrace = traces[nextIndex];
        if (nextTrace) {
          onSelect(nextTrace.id);
        }
      }

      if (event.key.toLowerCase() === "r") {
        event.preventDefault();
        if (!isRefreshing) {
          onRefresh();
        }
      }

      if ((event.key === "Delete" || event.key === "Backspace") && selectedTraceId) {
        if (isDeletingTrace) {
          return;
        }
        event.preventDefault();
        onDeleteTrace?.(selectedTraceId);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [traces, selectedTraceId, onSelect, onRefresh, isRefreshing, onDeleteTrace, isDeletingTrace]);

  const todayIso = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

  const ESTIMATED_ITEM_HEIGHT = 132;
  const overscan = 6;
  const totalHeight = traces.length * ESTIMATED_ITEM_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / ESTIMATED_ITEM_HEIGHT) - overscan);
  const endIndex = Math.min(
    traces.length,
    Math.ceil((scrollTop + viewportHeight) / ESTIMATED_ITEM_HEIGHT) + overscan
  );
  const virtualTraces = traces.slice(startIndex, endIndex);
  const paddingTop = startIndex * ESTIMATED_ITEM_HEIGHT;
  const paddingBottom = Math.max(0, totalHeight - endIndex * ESTIMATED_ITEM_HEIGHT);

  useEffect(() => {
    if (!selectedTraceId) {
      return;
    }
    const container = listRef.current;
    if (!container) {
      return;
    }
    const button = container.querySelector<HTMLButtonElement>(`button[data-trace-id="${selectedTraceId}"]`);
    button?.focus({ preventScroll: true });
  }, [selectedTraceId, virtualTraces]);

  const handleFilterChange = (key: keyof Filters, value?: string) => {
    const next: Filters = { ...filters, [key]: value || undefined };
    if (!value) {
      delete next[key];
    }

    if ((key === "startDate" || key === "endDate") && filters.reviewStatus === "today") {
      const nextStart = key === "startDate" ? (value || undefined) : next.startDate;
      const nextEnd = key === "endDate" ? (value || undefined) : next.endDate;
      if (nextStart !== todayIso || nextEnd !== todayIso) {
        delete next.reviewStatus;
      }
    }

    onFiltersChange(next);
  };

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(() =>
    Boolean(
      (filters.reviewStatus && filters.reviewStatus !== "today") ||
        (filters.startDate && filters.startDate !== todayIso) ||
        (filters.endDate && filters.endDate !== todayIso)
    )
  );
  const hasAdvancedFiltersActive = Boolean(
    (filters.reviewStatus && filters.reviewStatus !== "today") ||
      (filters.startDate && filters.startDate !== todayIso) ||
      (filters.endDate && filters.endDate !== todayIso)
  );
  useEffect(() => {
    if (!showDatePicker) return;
    const listener = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(".date-popover") || target?.closest(".date-trigger")) return;
      setShowDatePicker(false);
    };
    window.addEventListener("mousedown", listener);
    return () => window.removeEventListener("mousedown", listener);
  }, [showDatePicker]);

  useEffect(() => {
    if (!showAdvancedFilters) {
      setShowDatePicker(false);
    }
  }, [showAdvancedFilters]);

  const hasActiveFilters = Boolean(
    filters.severity ||
      filters.failureMode ||
      filters.channel ||
      (filters.reviewStatus && filters.reviewStatus !== "today") ||
      (filters.startDate && filters.startDate !== todayIso) ||
      (filters.endDate && filters.endDate !== todayIso)
  );

  const clearFilters = () => {
    setShowDatePicker(false);
    onFiltersChange({ reviewStatus: "today", startDate: todayIso, endDate: todayIso });
  };

  useEffect(() => {
    onTracesChange?.(traces);
  }, [traces, onTracesChange]);

  const statusOptions: Array<{ label: string; value?: string }> = useMemo(
    () => [
      { label: "Today", value: "today" },
      { label: "All", value: undefined },
      { label: "Pending", value: "needs_attention" },
      { label: "Done", value: "annotated" }
    ],
    []
  );

  const handleStatusSelect = (value?: string) => {
    if (value === "today") {
      onFiltersChange({
        ...filters,
        reviewStatus: "today",
        startDate: todayIso,
        endDate: todayIso
      });
      return;
    }

    const next: Filters = { ...filters };
    if (value) {
      next.reviewStatus = value;
    } else {
      delete next.reviewStatus;
      if (
        filters.reviewStatus === "today" &&
        filters.startDate === todayIso &&
        filters.endDate === todayIso
      ) {
        delete next.startDate;
        delete next.endDate;
      }
    }
    onFiltersChange(next);
  };

  const tapeChannel = useMemo(() => channels.find((name) => name.toLowerCase() === "tape"), [channels]);
  const isTapeActive = tapeChannel ? filters.channel === tapeChannel : false;

  const getAnnotationCounts = useCallback(
    (trace: Trace): TraceAnnotationCounts => {
      const base = annotationCountsByTrace?.[trace.id] ?? trace.annotationCounts;
      const counts: TraceAnnotationCounts = base
        ? { ...base }
        : {
            total: 0,
            needsAttention: 0,
            resolved: 0,
            positive: 0,
            informational: 0
          };

      if (counts.total === 0 && trace.reviewStatus) {
        const status = trace.reviewStatus.toLowerCase();
        if (status === "annotated" || status === "resolved") {
          counts.total = 1;
          counts.resolved = 1;
        } else if (status === "needs_attention" || status === "regressions") {
          counts.total = 1;
          counts.needsAttention = 1;
        }
      }

      return counts;
    },
    [annotationCountsByTrace]
  );

  return (
    <div className="trace-list">
      <section className="filters">
        <header className="filters__section-header">
          <div>
            <h2>Filters</h2>
            <p>Select the basics you care about most.</p>
          </div>
          {hasActiveFilters && (
            <button type="button" className="filters__clear" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </header>

        <div className="filters__status">
          <span>Status</span>
          <div className="filters__status-buttons">
            {statusOptions.map((option) => {
              const isActive = option.value === filters.reviewStatus || (!option.value && !filters.reviewStatus);
              return (
                <button
                  key={option.label}
                  type="button"
                  className={isActive ? "filters__status-button filters__status-button--active" : "filters__status-button"}
                  onClick={() => handleStatusSelect(option.value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {tapeChannel && (
          <div className="filters__status">
            <span>Channel focus</span>
            <div className="filters__status-buttons">
              <button
                type="button"
                className={isTapeActive ? "filters__status-button filters__status-button--active" : "filters__status-button"}
                onClick={() => handleFilterChange("channel", isTapeActive ? undefined : tapeChannel)}
              >
                Tape only
              </button>
            </div>
          </div>
        )}

        <div className="filters__grid filters__grid--primary">
          <label className="filter">
            <span>Severity</span>
            <select value={filters.severity ?? ""} onChange={(event) => handleFilterChange("severity", event.target.value || undefined)}>
              <option value="">All severities</option>
              <option value="blocker">Blocker</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </label>
          <label className="filter">
            <span>Failure mode</span>
            <select value={filters.failureMode ?? ""} onChange={(event) => handleFilterChange("failureMode", event.target.value || undefined)}>
              <option value="">All failure modes</option>
              {failureModes.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
          </label>
          <label className="filter">
            <span>Channel</span>
            <select value={filters.channel ?? ""} onChange={(event) => handleFilterChange("channel", event.target.value || undefined)}>
              <option value="">All channels</option>
              {channels.map((channel) => (
                <option key={channel} value={channel}>
                  {channel}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="filters__advanced">
          <button
            type="button"
            className="filters__advanced-toggle"
            onClick={() => setShowAdvancedFilters((value) => !value)}
            aria-expanded={showAdvancedFilters}
          >
            {showAdvancedFilters
              ? "Hide advanced filters"
              : hasAdvancedFiltersActive
                ? "Show advanced filters (active)"
                : "Show advanced filters"}
          </button>
          {showAdvancedFilters && (
            <div className="filters__grid filters__grid--advanced">
              <div className="filter review-toggle">
                <span>More queues</span>
                <div className="review-toggle__buttons">
                  {[
                    { label: "Needs attention", value: "needs_attention" },
                    { label: "Regressions", value: "regressions" },
                    { label: "Unannotated", value: "unannotated" },
                    { label: "Reviewed", value: "annotated" }
                  ].map((option) => (
                    <button
                      key={option.label}
                      type="button"
                      className={
                        option.value === filters.reviewStatus || (!option.value && !filters.reviewStatus)
                          ? "review-toggle__button review-toggle__button--active"
                          : "review-toggle__button"
                      }
                      onClick={() => handleStatusSelect(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="filter date-trigger">
                <span>Date range</span>
                <button type="button" onClick={() => setShowDatePicker((value) => !value)}>
                  {filters.startDate || filters.endDate ? `${filters.startDate ?? "…"} → ${filters.endDate ?? "…"}` : "All time"}
                </button>
                {showDatePicker && (
                  <div className="date-popover">
                    <label>
                      <span>Start</span>
                      <input
                        type="date"
                        value={filters.startDate ?? ""}
                        onChange={(event) => handleFilterChange("startDate", event.target.value || undefined)}
                      />
                    </label>
                    <label>
                      <span>End</span>
                      <input
                        type="date"
                        value={filters.endDate ?? ""}
                        onChange={(event) => handleFilterChange("endDate", event.target.value || undefined)}
                      />
                    </label>
                    <button type="button" onClick={() => setShowDatePicker(false)}>Close</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      </section>
      <header className="trace-list__section">
        <div>
          <h2>Traces</h2>
          <p className="muted">{traces.length ? `${traces.length} trace${traces.length === 1 ? "" : "s"} loaded for review.` : "Browse traces that match your filters."}</p>
        </div>
        <div className="trace-list__actions">
          <button type="button" className="trace-list__refresh" onClick={onRefresh} disabled={isRefreshing}>
            {isRefreshing ? "Refreshing…" : "Refresh"}
          </button>
          <button type="button" className="trace-list__export" onClick={onExport} disabled={isExporting}>
            {isExporting ? "Exporting…" : "Export"}
          </button>
        </div>
      </header>
      <div ref={listRef} className="trace-list__items" role="list" onScroll={handleScroll}>
        <div className="trace-list__virtual" style={{ paddingTop, paddingBottom }}>
          {query.isLoading && traces.length === 0 && <div className="trace-list__loading">Loading traces…</div>}
          {virtualTraces.map((trace) => (
            <TraceListItem
              key={trace.id}
              trace={trace}
              isSelected={trace.id === selectedTraceId}
              onClick={() => onSelect(trace.id)}
              annotationCounts={getAnnotationCounts(trace)}
            />
          ))}
          {query.isFetchingNextPage && <div className="trace-list__loading">Loading more…</div>}
          {!query.hasNextPage && traces.length === 0 && !query.isLoading && (
            <div className="trace-list__empty">No traces match the current filters.</div>
          )}
        </div>
      </div>
      {query.hasNextPage && (
        <button className="trace-list__load-more" onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
          {query.isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}

function TraceListItem({ trace, isSelected, onClick, annotationCounts }: { trace: Trace; isSelected: boolean; onClick: () => void; annotationCounts: TraceAnnotationCounts }) {
  const createdAtLabel = trace.createdAt ? formatDistanceToNow(new Date(trace.createdAt), { addSuffix: true }) : "";
  const totalAnnotations = annotationCounts.total;
  const hasPending = annotationCounts.needsAttention > 0;
  const hasResolved = annotationCounts.resolved > 0;
  const hasPositive = annotationCounts.positive > 0;
  const hasInformational = annotationCounts.informational > 0;

  const overallStatus = totalAnnotations === 0 ? "unannotated" : hasPending ? "pending" : "resolved";

  const itemClasses = [
    "trace-list-item",
    isSelected ? "trace-list-item--selected" : "",
    `trace-list-item--${overallStatus}`
  ]
    .filter(Boolean)
    .join(" ");

  const statusBadges = [] as Array<{ key: string; label: string; className: string }>;

  if (totalAnnotations === 0) {
    statusBadges.push({ key: "none", label: "No annotations", className: "trace-status trace-status--empty" });
  }
  if (hasPending) {
    statusBadges.push({ key: "pending", label: `Pending ${annotationCounts.needsAttention}`, className: "trace-status trace-status--pending" });
  }
  if (hasResolved) {
    statusBadges.push({ key: "resolved", label: `Resolved ${annotationCounts.resolved}`, className: "trace-status trace-status--resolved" });
  }
  if (hasPositive) {
    statusBadges.push({ key: "positive", label: `Positive ${annotationCounts.positive}`, className: "trace-status trace-status--positive" });
  }
  if (hasInformational) {
    statusBadges.push({ key: "informational", label: `Informational ${annotationCounts.informational}`, className: "trace-status trace-status--informational" });
  }

  return (
    <button className={itemClasses} onClick={onClick} data-trace-id={trace.id} aria-selected={isSelected}>
      <div className="trace-list-item__meta">
        <span className="trace-list-item__time">{createdAtLabel}</span>
        {trace.model && <span className="trace-list-item__model">{trace.model}</span>}
      </div>
      {statusBadges.length > 0 && (
        <div className="trace-list-item__statuses">
          {statusBadges.map((badge) => (
            <span key={badge.key} className={badge.className}>
              {badge.label}
            </span>
          ))}
        </div>
      )}
      <div className="trace-list-item__labels">
        {trace.userLabel && <span className="pill">{trace.userLabel}</span>}
        {trace.companyLabel && <span className="pill">{trace.companyLabel}</span>}
        {trace.channel && <span className="pill pill--outline">{trace.channel}</span>}
      </div>
      <dl className="trace-list-item__stats">
        {trace.latencyMs !== undefined && (
          <Fragment>
            <dt>Latency</dt>
            <dd>{trace.latencyMs} ms</dd>
          </Fragment>
        )}
        {trace.costUsd !== undefined && (
          <Fragment>
            <dt>Cost</dt>
            <dd>${trace.costUsd.toFixed(4)}</dd>
          </Fragment>
        )}
      </dl>
    </button>
  );
}
